import { canInsertNode, InputRule, mergeAttributes } from "@tiptap/core"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"
import { canSplit } from "@tiptap/pm/transform"
import TiptapHorizontalRule from "@tiptap/extension-horizontal-rule"

const HORIZONTAL_RULE_INPUT = /^(?:---|—-|___\s|\*\*\*\s)$/

const LIST_ITEM_TYPES = new Set(["listItem", "taskItem"])
const LIST_TYPES = new Set(["bulletList", "orderedList", "taskList"])

function isListItem(node) {
  return LIST_ITEM_TYPES.has(node?.type.name)
}

function isList(node) {
  return LIST_TYPES.has(node?.type.name)
}

function targetDepth($from) {
  for (let depth = $from.depth; depth > 0; depth--) {
    if (isListItem($from.node(depth))) return depth
  }

  return $from.depth
}

function replaceTargetDepth($from) {
  const depth = $from.depth
  const item = $from.node(depth - 1)

  if (isListItem(item) && item.childCount === 1 && isEmptyParagraph(item.firstChild)) {
    return depth - 1
  }

  return depth
}

function parentAllowsRule($from, depth, ruleType, indexOffset) {
  if (depth <= 0) return false

  const parent = $from.node(depth - 1)
  if (isListItem(parent)) return false

  const index = $from.index(depth - 1) + indexOffset
  return parent.canReplaceWith(index, index, ruleType)
}

function canReplaceBlockWithRule($from, depth, ruleType) {
  if (depth <= 0) return false

  const parent = $from.node(depth - 1)
  if (isListItem(parent)) return false

  const index = $from.index(depth - 1)
  return parent.canReplaceWith(index, index + 1, ruleType)
}

function isEmptyParagraph(node) {
  return node?.type.name === "paragraph" && node.content.size === 0
}

function continueOrderedListAfterRule(tr, aroundPos, ruleType, startAt) {
  if (!startAt || startAt <= 1) return

  const rulePos = findNearestRulePos(tr.doc, aroundPos, ruleType)
  if (rulePos == null) return

  const ruleNode = tr.doc.resolve(rulePos).nodeAfter
  if (ruleNode?.type.name !== "horizontalRule") return

  const listPos = rulePos + ruleNode.nodeSize
  const list = tr.doc.resolve(listPos).nodeAfter
  if (list?.type.name !== "orderedList") return

  tr.setNodeMarkup(listPos, undefined, { ...list.attrs, start: startAt })
}

function placeRuleAt(tr, pos, rule, continueStart) {
  const next = tr.doc.resolve(pos).nodeAfter
  if (isEmptyParagraph(next)) {
    tr.replaceWith(pos, pos + next.nodeSize, rule)
  } else {
    tr.insert(pos, rule)
  }

  continueOrderedListAfterRule(tr, pos, rule.type, continueStart)
  return findNearestRulePos(tr.doc, pos, rule.type) ?? pos
}

function walkOutAtEdges(tr, pos, ruleType) {
  for (let step = 0; step < 20; step++) {
    const $pos = tr.doc.resolve(Math.min(pos, tr.doc.content.size))
    const parent = $pos.parent
    if ($pos.depth === 0) return pos
    if (!isListItem(parent) && parent.canReplaceWith($pos.index(), $pos.index(), ruleType)) {
      return pos
    }
    if ($pos.index() === 0) {
      pos = $pos.before()
      continue
    }
    if ($pos.index() === parent.childCount) {
      pos = $pos.after()
      continue
    }
    return pos
  }

  return pos
}

function splitSurroundingListIfNeeded(tr, pos, ruleType) {
  const $pos = tr.doc.resolve(Math.min(pos, tr.doc.content.size))

  if (!isListItem($pos.parent) && $pos.parent.canReplaceWith($pos.index(), $pos.index(), ruleType)) {
    return { pos }
  }

  // walkOutAtEdges stops between two list items. Split there so the rule
  // sits between lists instead of being inserted into the list.
  if (isList($pos.parent) && canSplit(tr.doc, pos)) {
    const continueStart = ($pos.parent.attrs.start || 1) + $pos.index()
    tr.split(pos)
    return { pos: walkOutAtEdges(tr, pos, ruleType), continueStart }
  }

  return { pos }
}

function splitListAt(tr, $from, itemDepth, side) {
  const list = $from.node(itemDepth - 1)
  const itemIndex = $from.index(itemDepth - 1)
  const pos = side === "after" ? $from.after(itemDepth) : $from.before(itemDepth)
  const itemsBeforeSecond = side === "after" ? itemIndex + 1 : itemIndex

  tr.split(pos)
  return { pos, continueStart: (list.attrs.start || 1) + itemsBeforeSecond }
}

function insertRule(tr, $from, rule, side) {
  if (side === "replace") {
    const depth = replaceTargetDepth($from)

    if (canReplaceBlockWithRule($from, depth, rule.type)) {
      const from = $from.before(depth)
      tr.replaceWith(from, $from.after(depth), rule)
      return from
    }

    if (canReplaceBlockWithRule($from, depth - 1, rule.type) && $from.node(depth - 1).childCount === 1) {
      const from = $from.before(depth - 1)
      tr.replaceWith(from, $from.after(depth - 1), rule)
      return from
    }

    const parent = $from.node(depth - 1)
    const itemIndex = $from.index(depth - 1)
    const listCount = parent.childCount
    const from = $from.before(depth)
    tr.delete(from, $from.after(depth))
    let pos = tr.mapping.map(from)
    let continueStart
    if (itemIndex > 0 && itemIndex < listCount - 1) {
      // A middle empty paragraph inside a list item is a paragraph index, not
      // a list-item index. Split the item and its list, then walk out so the
      // rule sits between the two lists — inserting it under the list throws.
      if (isListItem(parent) && depth >= 2 && canSplit(tr.doc, pos, 2)) {
        const list = $from.node(depth - 2)
        continueStart = (list.attrs.start || 1) + $from.index(depth - 2) + 1
        tr.split(pos, 2)
        pos = walkOutAtEdges(tr, pos, rule.type)
      } else if (!isListItem(parent) && canSplit(tr.doc, pos)) {
        tr.split(pos)
        continueStart = (parent.attrs.start || 1) + itemIndex
      } else {
        pos = walkOutAtEdges(tr, pos, rule.type)
      }
    } else {
      pos = walkOutAtEdges(tr, pos, rule.type)
    }
    const lifted = splitSurroundingListIfNeeded(tr, pos, rule.type)
    return placeRuleAt(tr, lifted.pos, rule, continueStart ?? lifted.continueStart)
  }

  const depth = targetDepth($from)
  const indexOffset = side === "after" ? 1 : 0
  let insertDepth = depth

  while (insertDepth > 0 && !parentAllowsRule($from, insertDepth, rule.type, indexOffset)) {
    const container = $from.node(insertDepth - 1)
    const index = $from.index(insertDepth - 1)
    const atEdge = side === "after" ? index === container.childCount - 1 : index === 0

    if (!atEdge && isListItem($from.node(insertDepth))) {
      const split = splitListAt(tr, $from, insertDepth, side)
      return placeRuleAt(tr, split.pos, rule, split.continueStart)
    }

    insertDepth -= 1
  }

  if (insertDepth <= 0) return null

  const pos = side === "after" ? $from.after(insertDepth) : $from.before(insertDepth)
  return placeRuleAt(tr, pos, rule)
}

function canInsertRule($from, ruleType, side) {
  const indexOffset = side === "after" ? 1 : 0

  for (let depth = targetDepth($from); depth > 0; depth--) {
    if (parentAllowsRule($from, depth, ruleType, indexOffset)) return true
  }

  return false
}

function findNearestRulePos(doc, aroundPos, ruleType) {
  let nearest = null
  let nearestDistance = Infinity

  doc.descendants((node, pos) => {
    if (node.type !== ruleType) return

    const distance = Math.abs(pos - aroundPos)
    if (distance < nearestDistance) {
      nearest = pos
      nearestDistance = distance
    }
  })

  return nearest
}

function placeCaretAfterRule(tr, rulePos, rule, paragraphType) {
  const afterRule = rulePos + rule.nodeSize
  const $after = tr.doc.resolve(Math.min(afterRule, tr.doc.content.size))

  if ($after.nodeAfter?.isTextblock) {
    tr.setSelection(TextSelection.create(tr.doc, afterRule + 1))
  } else if ($after.nodeAfter?.isBlock) {
    tr.setSelection(NodeSelection.create(tr.doc, afterRule))
  } else if (paragraphType) {
    const paragraph = paragraphType.create()
    tr.insert(afterRule, paragraph)
    tr.setSelection(TextSelection.create(tr.doc, afterRule + 1))
  }
}

export const HorizontalRule = TiptapHorizontalRule.extend({
  addInputRules() {
    return [
      new InputRule({
        find: HORIZONTAL_RULE_INPUT,
        handler: ({ range, chain }) => {
          const applied = chain().deleteRange(range).setHorizontalRule().run()
          if (!applied) return null
        },
      }),
    ]
  },

  addCommands() {
    return {
      setHorizontalRule:
        () =>
        ({ state, tr, dispatch }) => {
          if (!canInsertNode(state, state.schema.nodes[this.name])) {
            return false
          }

          const { $from, empty } = state.selection
          const rule = this.type.create()
          const replaceEmptyParagraph =
            $from.parent.type.name === "paragraph" && $from.parent.content.size === 0
          const atEndOfBlock = $from.parentOffset === $from.parent.content.size
          const atStartOfBlock = $from.parentOffset === 0

          if (dispatch) {
            let rulePos

            if (replaceEmptyParagraph) {
              rulePos = insertRule(tr, $from, rule, "replace")
            } else if (empty && atEndOfBlock && canInsertRule($from, this.type, "after")) {
              rulePos = insertRule(tr, $from, rule, "after")
            } else if (empty && atStartOfBlock && canInsertRule($from, this.type, "before")) {
              rulePos = insertRule(tr, $from, rule, "before")
            } else {
              const from = $from.pos
              tr.replaceSelectionWith(rule)
              rulePos = findNearestRulePos(tr.doc, tr.mapping.map(from), this.type)
            }

            if (rulePos == null) return true

            placeCaretAfterRule(tr, rulePos, rule, state.schema.nodes.paragraph)
            tr.scrollIntoView()
          }

          return true
        },
    }
  },

  parseHTML() {
    return [
      // Claim our own serialized wrapper with a priority above the generic
      // html-preservation Div node, so save/reopen round trips don't nest an
      // extra div around the rule each time.
      { tag: `div[data-type="${this.name}"]`, priority: 100 },
      { tag: "hr" },
    ]
  },

  renderHTML() {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, { "data-type": this.name }),
      ["hr"],
    ];
  },
})

export default HorizontalRule
